import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, catchError, filter, interval, Observable, of, Subscription, switchMap, take } from 'rxjs';
import { map } from 'rxjs/operators';
import { TokenService } from 'src/app/core/interceptors/token.service';
import { environment } from 'src/environments/environment';

import { AnnouncementModel } from '../../core/models/announcement.model';

@Injectable({
  providedIn: 'root'
})
export class AnnouncementService {
  private apiUrl = `${environment.apiUrl}/loan/api/admin`;
  private currentAnnouncementSubject = new BehaviorSubject<AnnouncementModel | null>(null);
  public currentAnnouncement$ = this.currentAnnouncementSubject.asObservable();

  private pollingInterval = 60000; // 15 seconds
  private pollingSubscription?: Subscription;
  private tokenWatchSubscription?: Subscription;
  private streamAbortController?: AbortController;
  private reconnectTimeoutId?: ReturnType<typeof setTimeout>;
  private destroyed = false;
  private readonly announcementDebug = !environment.production;

  constructor(
    private http: HttpClient,
    private tokenService: TokenService
  ) {
    //this.connectToAnnouncementStream();
    this.startPolling();
    this.watchForTokenAndRefresh();
  }

  // CRUD Operations
  findAll(): Observable<AnnouncementModel[]> {
    return this.http.get<AnnouncementModel[]>(`${this.apiUrl}/findAllAnnouncements`);
  }

  findActive(): Observable<AnnouncementModel | null> {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return of(null);
    }

    if (!this.tokenService.hasValidToken()) {
      // Avoid clearing the banner while auth is still being established.
      return of(this.currentAnnouncementSubject.value);
    }

    return this.http.get<any>(`${this.apiUrl}/findActiveAnnouncement`).pipe(
      map((response) => this.normalizeActiveAnnouncement(response)),
      catchError((error: HttpErrorResponse) => {
        if (this.announcementDebug) {
          console.error('Error loading active announcement:', {
            status: error.status,
            statusText: error.statusText,
            url: error.url
          });
        }
        return of(null);
      })
    );
  }

  private normalizeActiveAnnouncement(response: any): AnnouncementModel | null {
    if (!response) {
      return null;
    }

    const candidate = response.announcement ?? response.data ?? response;
    if (!candidate) {
      return null;
    }

    if (Array.isArray(candidate)) {
      return candidate[0] ?? null;
    }

    if (typeof candidate !== 'object') {
      return null;
    }

    return candidate as AnnouncementModel;
  }

  save(announcement: Partial<AnnouncementModel>): Observable<AnnouncementModel> {
    return this.http.post<AnnouncementModel>(`${this.apiUrl}/saveAnnouncement`, announcement);
  }

  update(id: number, announcement: Partial<AnnouncementModel>): Observable<AnnouncementModel> {
    return this.http.post<AnnouncementModel>(`${this.apiUrl}/updateAnnouncement/${id}`, announcement);
  }

  sendAnnouncement(id: number, username: string): Observable<AnnouncementModel> {
    return this.http.post<AnnouncementModel>(`${this.apiUrl}/sendAnnouncement/${id}`, { username });
  }

  clearAnnouncement(id: number, username: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/clearAnnouncement/${id}`, { username });
  }

  delete(id: number, username: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/deleteAnnouncement/${id}`, {
      body: { username }
    });
  }

  getAuditTrail(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/getAnnouncementAuditTrail`);
  }

  getAuditTrailByAnnouncementId(id: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/getAnnouncementAuditTrail/${id}`);
  }

  // private connectToAnnouncementStream(): void {
  //   if (this.destroyed) {
  //     return;
  //   }

  //   this.clearReconnectTimer();

  //   const token = this.tokenService.getToken();

  //   if (!token || typeof fetch === 'undefined' || typeof TextDecoder === 'undefined') {
  //     this.startPolling();
  //     this.scheduleReconnect();
  //     return;
  //   }

  //   this.stopPolling();
  //   this.streamAbortController?.abort();
  //   this.streamAbortController = new AbortController();

  //   void this.listenToAnnouncementStream(token, this.streamAbortController.signal);
  // }

  // private async listenToAnnouncementStream(token: string, signal: AbortSignal): Promise<void> {
  //   try {
  //     const response = await fetch(this.subscriptionUrl, {
  //       headers: {
  //         Accept: 'text/event-stream',
  //         Authorization: `Bearer ${token}`,
  //       },
  //       cache: 'no-store',
  //       signal,
  //     });

  //     if (!response.ok || !response.body) {
  //       throw new Error(`Announcement stream failed with status ${response.status}`);
  //     }

  //     const reader = response.body.getReader();
  //     const decoder = new TextDecoder();
  //     let buffer = '';

  //     while (!signal.aborted) {
  //       const { value, done } = await reader.read();

  //       if (done) {
  //         break;
  //       }

  //       buffer += decoder.decode(value, { stream: true });
  //       buffer = this.processStreamBuffer(buffer);
  //     }

  //     if (!signal.aborted) {
  //       throw new Error('Announcement stream closed unexpectedly');
  //     }
  //   } catch (error) {
  //     if (signal.aborted || this.destroyed) {
  //       return;
  //     }

  //     console.error('Error subscribing to announcement stream:', error);
  //     this.startPolling();
  //     this.scheduleReconnect();
  //   }
  // }

  // private processStreamBuffer(buffer: string): string {
  //   const normalizedBuffer = buffer.replace(/\r\n/g, '\n');
  //   const chunks = normalizedBuffer.split('\n\n');
  //   const remainder = chunks.pop() ?? '';

  //   for (const chunk of chunks) {
  //     this.processStreamMessage(chunk);
  //   }

  //   return remainder;
  // }

  // private processStreamMessage(chunk: string): void {
  //   const dataLines = chunk
  //     .split('\n')
  //     .filter(line => line.startsWith('data:'))
  //     .map(line => line.slice(5).trimStart());

  //   if (dataLines.length === 0) {
  //     return;
  //   }

  //   const payload = dataLines.join('\n').trim();

  //   if (!payload || payload === 'heartbeat') {
  //     return;
  //   }

  //   try {
  //     const announcement = payload === 'null'
  //       ? null
  //       : JSON.parse(payload) as AnnouncementModel;

  //     this.currentAnnouncementSubject.next(announcement);
  //   } catch (error) {
  //     console.error('Failed to parse announcement stream payload:', payload, error);
  //   }
  // }

  // private scheduleReconnect(): void {
  //   if (this.destroyed || this.reconnectTimeoutId) {
  //     return;
  //   }

  //   this.reconnectTimeoutId = setTimeout(() => {
  //     this.reconnectTimeoutId = undefined;
  //     this.connectToAnnouncementStream();
  //   }, 5000);
  // }

  // private clearReconnectTimer(): void {
  //   if (this.reconnectTimeoutId) {
  //     clearTimeout(this.reconnectTimeoutId);
  //     this.reconnectTimeoutId = undefined;
  //   }
  // }

  private startPolling(): void {
    // Initial fetch
    this.fetchActiveAnnouncement();

    // Avoid creating duplicate intervals when service is re-instantiated in tests.
    this.pollingSubscription?.unsubscribe();

    // Poll every 15 seconds
    this.pollingSubscription = interval(this.pollingInterval).pipe(
      switchMap(() => this.fetchActiveAnnouncement()),
      catchError(error => {
        console.error('Error polling announcements:', error);
        return of(null);
      })
    ).subscribe();
  }

  private watchForTokenAndRefresh(): void {
    this.tokenWatchSubscription?.unsubscribe();

    this.tokenWatchSubscription = interval(5000).pipe(
      filter(() => this.tokenService.hasValidToken()),
      take(1),
      switchMap(() => this.fetchActiveAnnouncement())
    ).subscribe();
  }

  private stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = undefined;
    this.tokenWatchSubscription?.unsubscribe();
    this.tokenWatchSubscription = undefined;
  }

  private fetchActiveAnnouncement(): Observable<AnnouncementModel | null> {
    return this.findActive().pipe(
      map(announcement => {
        this.currentAnnouncementSubject.next(announcement);
        return announcement;
      })
    );
  }

  refresh(): void {
    this.fetchActiveAnnouncement().subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    //this.clearReconnectTimer();
    this.streamAbortController?.abort();
    this.stopPolling();
  }
}
